// Visitor tracking utility functions for analytics
import { supabase } from "../supabaseClient";

/**
 * Generate a unique visitor ID using crypto.randomUUID() or fallback
 * @returns {string} Unique visitor ID
 */
export const generateVisitorId = () => {
  // Use crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback UUID v4 generator for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Set visitor ID cookie with secure options
 * @param {string} visitorId - Unique visitor ID
 */
export const setVisitorCookie = (visitorId) => {
  const maxAge = 31536000; // 1 year in seconds
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const sameSite = '; SameSite=Lax';

  document.cookie = `visitorId=${visitorId}; path=/; max-age=${maxAge}${secure}${sameSite}`;

  // Also store in localStorage as backup
  localStorage.setItem('visitorId', visitorId);

  console.log('Visitor ID set:', visitorId);
};

/**
 * Get visitor ID from cookie or localStorage
 * @returns {string|null} Visitor ID or null if not found
 */
export const getVisitorIdFromCookie = () => {
  const match = document.cookie.match(/visitorId=([^;]+)/);
  const cookieId = match ? match[1] : null;

  // Check localStorage as fallback
  const localStorageId = localStorage.getItem('visitorId');

  return cookieId || localStorageId;
};

/**
 * Initialize visitor tracking - creates new ID if doesn't exist
 * @returns {string} Visitor ID
 */
export const initializeVisitorTracking = () => {
  let visitorId = getVisitorIdFromCookie();

  if (!visitorId) {
    // New visitor - generate and set unique ID
    visitorId = generateVisitorId();
    setVisitorCookie(visitorId);

    // Track first visit
    console.log('New visitor detected:', visitorId);

    // You can add analytics tracking here later
    // trackEvent('first_visit', { visitorId });

    return visitorId;
  } else {
    // Returning visitor
    console.log('Returning visitor:', visitorId);

    // Refresh localStorage backup if missing
    if (!localStorage.getItem('visitorId')) {
      localStorage.setItem('visitorId', visitorId);
    }

    return visitorId;
  }
};

/**
 * Get current visitor ID (for use throughout the app)
 * @returns {string|null} Current visitor ID
 */
export const getCurrentVisitorId = () => {
  return getVisitorIdFromCookie();
};

/**
 * Track page view with visitor ID
 * @param {string} pageName - Name of the page
 * @param {object} additionalData - Additional tracking data
 */
export const trackPageView = (pageName, additionalData = {}) => {
  const visitorId = getCurrentVisitorId();

  if (visitorId) {
    console.log('Page view tracked:', {
      page: pageName,
      visitorId,
      timestamp: new Date().toISOString(),
      ...additionalData
    });

    // Here you can add your analytics service integration
    // For example: analytics.track('page_view', { page: pageName, visitorId, ...additionalData });
  }
};

/**
 * Log analytics event to Supabase PageEvent table
 * @param {string} type - Event type (PAGE_VIEW, SEARCH_QUERY, FILTER, EVENT_VIEW)
 * @param {string} value - Event value (depends on type)
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const logAnalyticsEvent = async (type, value, userId = null) => {
  try {
    const visitorId = getCurrentVisitorId();

    if (!visitorId) {
      console.warn('No visitor ID available for analytics tracking');
      return;
    }

    const { data, error } = await supabase
      .from('PageEvent')
      .insert({
        type,
        value,
        user_cookie: visitorId,
        user_id: userId
      });

    if (error) {
      console.error('Error logging analytics event:', error);
    } else {
      console.log('Analytics event logged:', { type, value, visitorId, userId });
    }
  } catch (error) {
    console.error('Error in logAnalyticsEvent:', error);
  }
};

/**
 * Track page view event to Supabase
 * @param {string} pageValue - Page value (GENERAL, EVENTS, DEALS, COMMUNITY_CHATS, ACCOUNT, SPIN_WIN)
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackPageViewAnalytics = async (pageValue, userId = null) => {
  await logAnalyticsEvent('PAGE_VIEW', pageValue, userId);
};

/**
 * Track search query event to Supabase
 * @param {string} searchQuery - The search term
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackSearchQueryAnalytics = async (searchQuery, userId = null) => {
  await logAnalyticsEvent('SEARCH_QUERY', searchQuery, userId);
};

/**
 * Track filter event to Supabase
 * @param {string} filterType - Filter type (CITY, PRICE, DATE, VENUE, EVENT_TYPE, GENRE, AGE)
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackFilterAnalytics = async (filterType, userId = null) => {
  await logAnalyticsEvent('FILTER', filterType, userId);
};

/**
 * Track event view to Supabase
 * @param {string} eventId - The event ID
 * @param {string|null} userId - Logged in user ID (nullable)
 */
export const trackEventViewAnalytics = async (eventId, userId = null) => {
  await logAnalyticsEvent('EVENT_VIEW', eventId, userId);
};